import { ExternalLinkIcon } from '@heroicons/react/outline'
import clsx from 'clsx'
import Link from 'next/link'

import { track } from 'web/lib/service/analytics'
import {
  MOD_ACTION_TEXT_CLASS,
  ModActionBadge,
} from 'web/components/moderation/mod-action'

export type NavItem = {
  name: string
  /** Analytics name for clicks, when it should differ from the label. */
  trackingEventName?: string
  href?: string
  onClick?: () => void
  icon?: React.ComponentType<{ className?: string }>
  solidIcon?: React.ComponentType<{ className?: string }>
  iconClassName?: string
  external?: boolean
  alwaysShowName?: boolean
  prefetch?: boolean
  children?: React.ReactNode
  /** Optional secondary label rendered next to `name` (hidden on very narrow viewports). */
  subLabel?: string
  /** Optional class applied to the nav item container (e.g. for tighter padding). */
  itemClassName?: string
  /** Only shown because the user is a mod or admin. */
  modAction?: boolean
  /** Show a waving flag spotlight behind the item that follows the cursor while hovered. */
  flagHover?: boolean
}

export function SidebarItem(props: { item: NavItem; currentPage?: string }) {
  const { item, currentPage } = props

  const currentBasePath = '/' + (currentPage?.split('/')[1] ?? '')
  const queryCleanedHref =
    (item.href?.includes('?') ? item.href.split('?')[0] : item.href) ?? ''
  const segmentCleanedHref =
    queryCleanedHref.split('/').length > 2
      ? '/' + queryCleanedHref.split('/')[1]
      : queryCleanedHref
  const isCurrentPage =
    currentBasePath === segmentCleanedHref && !item.href?.startsWith('https://')

  const onMouseMove = item.flagHover
    ? (e: React.MouseEvent<HTMLElement>) => {
        const el = e.currentTarget
        const rect = el.getBoundingClientRect()
        el.style.setProperty('--flag-x', `${e.clientX - rect.left}px`)
        el.style.setProperty('--flag-y', `${e.clientY - rect.top}px`)
      }
    : undefined

  const onClick = () => {
    track('sidebar: ' + (item.trackingEventName ?? item.name))
    item.onClick?.()
  }

  const sidebarClass = clsx(
    item.modAction
      ? [
          MOD_ACTION_TEXT_CLASS,
          isCurrentPage ? 'bg-ink-200' : 'hover:bg-primary-100',
        ]
      : isCurrentPage
      ? 'bg-ink-200 text-ink-900'
      : 'text-ink-600 hover:bg-primary-100 hover:text-ink-700',
    'group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium',
    'focus-visible:bg-primary-100 outline-none',
    item.flagHover && 'flag-hover-item'
  )

  const sidebarItem = (
    <>
      {item.icon && (
        <item.icon
          className={clsx(
            item.modAction
              ? undefined
              : isCurrentPage
              ? 'text-ink-600'
              : 'text-ink-500 group-hover:text-ink-600',
            '  -ml-1 mr-3 h-6 w-6 flex-shrink-0',
            item.iconClassName
          )}
          aria-hidden="true"
        />
      )}
      <span className="truncate">{item.children ?? item.name}</span>
      {item.external && (
        <ExternalLinkIcon className="ml-2 h-4 w-4" aria-hidden="true" />
      )}
      {item.modAction && <ModActionBadge className="ml-auto pl-2" />}
    </>
  )

  if (item.href) {
    return (
      <Link
        href={item.href}
        aria-current={isCurrentPage ? 'page' : undefined}
        onClick={onClick}
        onMouseMove={onMouseMove}
        className={sidebarClass}
        target={
          item.external || !item.href.startsWith('/') ? '_blank' : undefined
        }
      >
        {sidebarItem}
      </Link>
    )
  } else {
    return (
      <button
        type="button"
        onClick={onClick}
        onMouseMove={onMouseMove}
        className={sidebarClass}
      >
        {sidebarItem}
      </button>
    )
  }
}
